import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelRadio as _vModelRadio, withDirectives as _withDirectives, vModelSelect as _vModelSelect, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, unref as _unref, createVNode as _createVNode, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "bg-blue-50 p-4 rounded-lg border border-blue-200" }
const _hoisted_2 = { class: "flex space-x-4" }
const _hoisted_3 = { class: "flex items-center" }
const _hoisted_4 = { class: "flex items-center" }
const _hoisted_5 = {
  key: 0,
  class: "bg-gray-50 p-4 rounded-lg border"
}
const _hoisted_6 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_7 = { class: "relative" }
const _hoisted_8 = { class: "relative" }
const _hoisted_9 = ["value"]
const _hoisted_10 = {
  key: 1,
  class: "bg-gray-50 p-4 rounded-lg border"
}
const _hoisted_11 = { class: "mb-6" }
const _hoisted_12 = {
  key: 0,
  class: "mt-1 text-sm text-red-600"
}
const _hoisted_13 = { class: "space-y-4" }
const _hoisted_14 = { class: "grid grid-cols-6 gap-3" }
const _hoisted_15 = ["onUpdate:modelValue"]
const _hoisted_16 = { class: "flex justify-end pt-4 border-t border-gray-200" }

import { ref, reactive, computed, onMounted, watch } from 'vue'
import { DataCard, ActionButton } from '@/components'
import { useNotification } from '@/composables/useNotification'
import type { CreateGammeData, GammeComplete } from '@/types'

// Props
interface Props {
  allDeclinaisons: GammeComplete[]
}

interface Emits {
  (e: 'create', data: CreateGammeData): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GammeCreationCard',
  props: {
    allDeclinaisons: {}
  },
  emits: ["create"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Notifications
const { error: showError } = useNotification()

const isSubmitting = ref(false)

// Mode de création
const creationMode = ref<'template' | 'custom'>('custom')

// Données pour les templates eFashion
const selectedNbDeclinaisons = ref('all')
const selectedTemplate = ref('')

// Déclinaisons filtrées
const filteredDeclinaisons = ref<GammeComplete[]>([])

const getDeclinaisonSizeCount = (declinaison: GammeComplete): number => {
  const sizes = [
    declinaison.d1_FR,
    declinaison.d2_FR,
    declinaison.d3_FR,
    declinaison.d4_FR,
    declinaison.d5_FR,
    declinaison.d6_FR,
    declinaison.d7_FR,
    declinaison.d8_FR,
    declinaison.d9_FR,
    declinaison.d10_FR,
    declinaison.d11_FR,
    declinaison.d12_FR,
  ]

  return sizes.filter((size) => size !== null && size !== undefined && size.trim() !== '').length
}

// Filtrer les déclinaisons selon le nombre de tailles sélectionné
const filterDeclinaisons = () => {
  selectedTemplate.value = '' // Réinitialiser la sélection
  
  // Filtrer les déclinaisons qui ont un titre valide
  const validDeclinaisons = props.allDeclinaisons.filter(d => d.titre && d.titre.trim() !== '')
  
  if (selectedNbDeclinaisons.value === 'all') {
    filteredDeclinaisons.value = validDeclinaisons
  } else {
    const targetCount = Number(selectedNbDeclinaisons.value)
    filteredDeclinaisons.value = validDeclinaisons.filter(
      (declinaison) => getDeclinaisonSizeCount(declinaison) === targetCount,
    )
  }
}

const formData = reactive<CreateGammeData>({
  titre: '',
  d1_FR: null,
  d2_FR: null,
  d3_FR: null,
  d4_FR: null,
  d5_FR: null,
  d6_FR: null,
  d7_FR: null,
  d8_FR: null,
  d9_FR: null,
  d10_FR: null,
  d11_FR: null,
  d12_FR: null,
})

// Colonnes de tailles pour la création personnalisée
const sizeColumns = ref<(string | null)[]>([
  null, null, null, null, null, null,
  null, null, null, null, null, null
])

const errors = reactive({
  titre: ''
})

// Vérifier si le formulaire est valide
const isFormValid = computed(() => {
  // Le titre doit être rempli
  if (!formData.titre || !formData.titre.trim()) {
    return false
  }
  
  // Au moins une colonne de taille doit être renseignée
  const hasAtLeastOneSize = sizeColumns.value.some(size => size && size.trim() !== '')
  
  return hasAtLeastOneSize
})

// Charger un template existant
const loadTemplate = () => {
  if (selectedTemplate.value) {
    const template = props.allDeclinaisons.find(d => d.id_declinaison.toString() === selectedTemplate.value)
    if (template) {
      formData.titre = template.titre || ''
      
      // Charger aussi les colonnes de tailles du template
      sizeColumns.value = [
        template.d1_FR,
        template.d2_FR,
        template.d3_FR,
        template.d4_FR,
        template.d5_FR,
        template.d6_FR,
        template.d7_FR,
        template.d8_FR,
        template.d9_FR,
        template.d10_FR,
        template.d11_FR,
        template.d12_FR,
      ]
    }
  } else {
    formData.titre = ''
  }
}

// Initialiser les déclinaisons filtrées
onMounted(() => {
  filterDeclinaisons()
})

// Watcher pour les props
watch(() => props.allDeclinaisons, () => {
  filterDeclinaisons()
}, { immediate: true })

// Synchroniser sizeColumns avec formData
watch(sizeColumns, (newColumns) => {
  formData.d1_FR = newColumns[0] || null
  formData.d2_FR = newColumns[1] || null
  formData.d3_FR = newColumns[2] || null
  formData.d4_FR = newColumns[3] || null
  formData.d5_FR = newColumns[4] || null
  formData.d6_FR = newColumns[5] || null
  formData.d7_FR = newColumns[6] || null
  formData.d8_FR = newColumns[7] || null
  formData.d9_FR = newColumns[8] || null
  formData.d10_FR = newColumns[9] || null
  formData.d11_FR = newColumns[10] || null
  formData.d12_FR = newColumns[11] || null
}, { deep: true })

// Réinitialiser le formulaire
const resetForm = () => {
  formData.titre = ''
  formData.d1_FR = null
  formData.d2_FR = null
  formData.d3_FR = null
  formData.d4_FR = null
  formData.d5_FR = null
  formData.d6_FR = null
  formData.d7_FR = null
  formData.d8_FR = null
  formData.d9_FR = null
  formData.d10_FR = null
  formData.d11_FR = null
  formData.d12_FR = null
  
  selectedTemplate.value = ''
  selectedNbDeclinaisons.value = 'all'
  
  // Réinitialiser les colonnes de tailles
  sizeColumns.value = [
    null, null, null, null, null, null,
    null, null, null, null, null, null
  ]
  
  errors.titre = ''
}

const validateForm = () => {
  errors.titre = ''
  
  if (!formData.titre.trim()) {
    errors.titre = 'Le nom de la gamme est requis'
    return false
  }
  
  // Vérifier qu'au moins une taille est renseignée
  const hasAtLeastOneSize = sizeColumns.value.some(size => size && size.trim() !== '')
  if (!hasAtLeastOneSize) {
    showError('Au moins une taille doit être renseignée')
    return false
  }
  
  return true
}

const handleSubmit = async () => {
  if (!validateForm()) return

  isSubmitting.value = true
  
  try {
    // formData est déjà synchronisé avec sizeColumns via le watcher
    emit('create', { ...formData })
    
    // Réinitialiser le formulaire
    resetForm()
  } catch (error) {
    showError('Erreur lors de la création de la gamme')
  } finally {
    isSubmitting.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(DataCard), {
    title: "Choisir ou créer une gamme de tailles",
    icon: "fas fa-plus-circle",
    "icon-color": "text-green-600"
  }, {
    default: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _withModifiers(handleSubmit, ["prevent"]),
        class: "space-y-6"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _cache[7] || (_cache[7] = _createElementVNode("h4", { class: "text-lg font-semibold text-blue-900 mb-4" }, "Mode de création", -1)),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("label", _hoisted_3, [
              _withDirectives(_createElementVNode("input", {
                type: "radio",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((creationMode).value = $event)),
                value: "custom",
                class: "mr-2"
              }, null, 512), [
                [_vModelRadio, creationMode.value]
              ]),
              _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-sm font-medium text-gray-700" }, "Créer ma propre déclinaison", -1))
            ]),
            _createElementVNode("label", _hoisted_4, [
              _withDirectives(_createElementVNode("input", {
                type: "radio",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((creationMode).value = $event)),
                value: "template",
                class: "mr-2"
              }, null, 512), [
                [_vModelRadio, creationMode.value]
              ]),
              _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-sm font-medium text-gray-700" }, "Utiliser une déclinaison existante", -1))
            ])
          ])
        ]),
        (creationMode.value === 'template')
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _cache[12] || (_cache[12] = _createElementVNode("h4", { class: "text-lg font-semibold text-gray-900 mb-4" }, "Déclinaison Existante", -1)),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", null, [
                  _cache[9] || (_cache[9] = _createElementVNode("label", {
                    for: "nb-declinaisons",
                    class: "block text-sm font-medium text-gray-700 mb-2"
                  }, " Combien avez vous de déclinaison ? ", -1)),
                  _createElementVNode("div", _hoisted_7, [
                    _withDirectives(_createElementVNode("select", {
                      id: "nb-declinaisons",
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedNbDeclinaisons).value = $event)),
                      onChange: filterDeclinaisons,
                      class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    }, _cache[8] || (_cache[8] = [
                      _createElementVNode("option", { value: "0" }, "0", -1),
                      _createElementVNode("option", { value: "1" }, "1", -1),
                      _createElementVNode("option", { value: "2" }, "2", -1),
                      _createElementVNode("option", { value: "3" }, "3", -1),
                      _createElementVNode("option", { value: "4" }, "4", -1),
                      _createElementVNode("option", { value: "5" }, "5", -1),
                      _createElementVNode("option", { value: "6" }, "6", -1),
                      _createElementVNode("option", { value: "7" }, "7", -1),
                      _createElementVNode("option", { value: "8" }, "8", -1),
                      _createElementVNode("option", { value: "9" }, "9", -1),
                      _createElementVNode("option", { value: "10" }, "10", -1),
                      _createElementVNode("option", { value: "11" }, "11", -1),
                      _createElementVNode("option", { value: "12" }, "12", -1),
                      _createElementVNode("option", { value: "all" }, "Affichez tout", -1)
                    ]), 544), [
                      [_vModelSelect, selectedNbDeclinaisons.value]
                    ])
                  ])
                ]),
                _createElementVNode("div", null, [
                  _cache[11] || (_cache[11] = _createElementVNode("label", {
                    for: "liste-declinaisons",
                    class: "block text-sm font-medium text-gray-700 mb-2"
                  }, " Liste des déclinaisons ", -1)),
                  _createElementVNode("div", _hoisted_8, [
                    _withDirectives(_createElementVNode("select", {
                      id: "liste-declinaisons",
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedTemplate).value = $event)),
                      onChange: loadTemplate,
                      class: "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    }, [
                      _cache[10] || (_cache[10] = _createElementVNode("option", { value: "" }, "-", -1)),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredDeclinaisons.value, (declinaison) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: declinaison.id_declinaison,
                          value: declinaison.id_declinaison
                        }, _toDisplayString(declinaison.titre || 'Sans titre'), 9, _hoisted_9))
                      }), 128))
                    ], 544), [
                      [_vModelSelect, selectedTemplate.value]
                    ])
                  ])
                ])
              ])
            ]))
          : _createCommentVNode("", true),
        (creationMode.value === 'custom')
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _cache[15] || (_cache[15] = _createElementVNode("h4", { class: "text-lg font-semibold text-gray-900 mb-4" }, "Création Personnalisée", -1)),
              _createElementVNode("div", _hoisted_11, [
                _cache[13] || (_cache[13] = _createElementVNode("label", {
                  for: "titre",
                  class: "block text-sm font-medium text-gray-700 mb-2"
                }, " Nom de la gamme * ", -1)),
                _withDirectives(_createElementVNode("input", {
                  id: "titre",
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.titre) = $event)),
                  type: "text",
                  required: "",
                  placeholder: "Ex: T-shirts Homme, Robes Femme, Chaussures...",
                  class: _normalizeClass(["w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500", { 'border-red-500': errors.titre }])
                }, null, 2), [
                  [_vModelText, formData.titre]
                ]),
                (errors.titre)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(errors.titre), 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_13, [
                _cache[14] || (_cache[14] = _createElementVNode("div", { class: "flex items-center justify-between" }, [
                  _createElementVNode("label", { class: "block text-sm font-medium text-gray-700" }, " Veuillez renseigner les tailles "),
                  _createElementVNode("div", { class: "text-sm text-gray-500" }, " Colonnes d1 à d12 ")
                ], -1)),
                _createElementVNode("div", _hoisted_14, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sizeColumns.value, (size, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: index,
                      class: "text-center"
                    }, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["px-2 py-1 rounded-md text-sm font-medium border", size ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'])
                      }, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((sizeColumns.value[index]) = $event),
                          type: "text",
                          placeholder: "-",
                          class: "w-full text-center bg-transparent border-none outline-none text-sm"
                        }, null, 8, _hoisted_15), [
                          [_vModelText, sizeColumns.value[index]]
                        ])
                      ], 2)
                    ]))
                  }), 128))
                ])
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_16, [
          _createVNode(_unref(ActionButton), {
            label: "Créer la gamme",
            icon: "fas fa-plus",
            variant: "primary",
            type: "submit",
            loading: isSubmitting.value,
            disabled: !isFormValid.value
          }, null, 8, ["loading", "disabled"])
        ])
      ], 32)
    ]),
    _: 1
  }))
}
}

})